export interface MagneticNavEffectOptions {
  /** Max distance (px) from a link edge to start pulling */
  distance?: number;
  /** How strongly the link moves toward the cursor */
  strength?: number;
  /** Clamp translation (px) to avoid huge jumps */
  maxOffset?: number;
  /** Spring-like follow factor per animation frame (0..1) */
  spring?: number;
}

type LinkState = {
  el: HTMLElement;
  currentX: number;
  currentY: number;
  targetX: number;
  targetY: number;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function distanceToRect(x: number, y: number, rect: DOMRect) {
  // Closest point on the rect to the pointer
  const clampedX = clamp(x, rect.left, rect.right);
  const clampedY = clamp(y, rect.top, rect.bottom);
  const dx = x - clampedX;
  const dy = y - clampedY;
  return Math.hypot(dx, dy);
}

export function applyMagneticNavEffect(
  rootEl: HTMLElement,
  linkEls: HTMLElement[],
  options: MagneticNavEffectOptions = {}
): () => void {
  const opts: Required<MagneticNavEffectOptions> = {
    distance: options.distance ?? 30,
    strength: options.strength ?? 0.28,
    maxOffset: options.maxOffset ?? 12,
    spring: options.spring ?? 0.12,
  };

  const states: LinkState[] = linkEls.map((el) => ({
    el,
    currentX: 0,
    currentY: 0,
    targetX: 0,
    targetY: 0,
  }));

  for (const s of states) {
    s.el.style.willChange = "transform";
    s.el.style.transform = "translate3d(0,0,0)";
  }

  let rafId = 0;
  let running = true;

  const setAllTargetsToZero = () => {
    for (const s of states) {
      s.targetX = 0;
      s.targetY = 0;
    }
  };

  const updateTargetsFromPointer = (clientX: number, clientY: number) => {
    for (const s of states) {
      const rect = s.el.getBoundingClientRect();
      const d = distanceToRect(clientX, clientY, rect);
      if (d > opts.distance) {
        s.targetX = 0;
        s.targetY = 0;
        continue;
      }

      const influence = 1 - d / opts.distance; // 0..1
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      // Move toward the cursor (cursor -> center vector)
      const dx = clientX - cx;
      const dy = clientY - cy;

      const rawX = dx * opts.strength * influence;
      const rawY = dy * opts.strength * influence;

      s.targetX = clamp(rawX, -opts.maxOffset, opts.maxOffset);
      s.targetY = clamp(rawY, -opts.maxOffset, opts.maxOffset);
    }
  };

  const tick = () => {
    if (!running) return;

    for (const s of states) {
      // Spring-like smoothing toward target
      s.currentX += (s.targetX - s.currentX) * opts.spring;
      s.currentY += (s.targetY - s.currentY) * opts.spring;

      s.el.style.transform = `translate3d(${s.currentX.toFixed(2)}px, ${s.currentY.toFixed(
        2
      )}px, 0)`;
    }

    rafId = window.requestAnimationFrame(tick);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    updateTargetsFromPointer(e.clientX, e.clientY);
  };

  const onPointerLeave = (e: PointerEvent) => {
    // Snap back when leaving the nav root
    if (e.pointerType !== "mouse") return;
    setAllTargetsToZero();
  };

  rootEl.addEventListener("pointermove", onPointerMove, { passive: true });
  rootEl.addEventListener("pointerleave", onPointerLeave, { passive: true });

  rafId = window.requestAnimationFrame(tick);

  return () => {
    running = false;
    rootEl.removeEventListener("pointermove", onPointerMove);
    rootEl.removeEventListener("pointerleave", onPointerLeave);
    if (rafId) window.cancelAnimationFrame(rafId);

    // Cleanup transforms
    for (const s of states) {
      s.el.style.transform = "translate3d(0,0,0)";
      s.el.style.willChange = "";
    }
  };
}

